import {
	Currency,
	currencySchema,
	type FinancialPeriod as FinancialPeriodTypes,
	Formula,
} from "@inrev/common";
import * as z from "zod";

export const adjustmentSourceSchema = z.enum(["auto", "manual"]);
export type AdjustmentSource = z.infer<typeof adjustmentSourceSchema>;

export const adjustmentEffectKindSchema = z.enum(["line", "dropped", "removed"]);
export type AdjustmentEffectKind = z.infer<typeof adjustmentEffectKindSchema>;

export const adjustmentSchema = z.strictObject({
	value: currencySchema,
	formula: z.string(),
	source: adjustmentSourceSchema,
	note: z.string().nullable(),
	effects: z.array(
		z.strictObject({
			kind: adjustmentEffectKindSchema,
			amount: currencySchema,
		}),
	),
});
export type Adjustment = z.infer<typeof adjustmentSchema>;

const layerBaseSchema = z.strictObject({
	allowed: currencySchema.nullable(),
	given: currencySchema.nullable(),
	extracted: currencySchema.nullable(),
});

export const layeredLeafSchema = layerBaseSchema.extend({
	adjustment: adjustmentSchema.nullable(),
});
export type LayeredLeaf = z.infer<typeof layeredLeafSchema>;

/**
 * A rollup carries no adjustment of its own: `buildSumRollupView` defines `allowed` as `given`
 * plus the summed child adjustments, so the adjustment is `allowed - given` and does not need a
 * field. The per-line `adjustment` on a leaf is where the formula and effects live.
 */
export const layeredTotalSchema = layerBaseSchema;
export type LayeredTotal = z.infer<typeof layeredTotalSchema>;

type AdjustmentEffect = Adjustment["effects"][number];

const adjustmentEffects = (given: number, formula: Formula): AdjustmentEffect[] => {
	const { lineDelta, droppedDelta, removedDelta } = Formula.contributions(given, formula);
	if (Number.isNaN(lineDelta) || Number.isNaN(droppedDelta) || Number.isNaN(removedDelta)) {
		return [];
	}

	// `lineDelta` is the whole change to the line, and the dropped and removed terms are already
	// inside it, so the line's own effect is what is left once they are taken back out. Reported
	// this way the three amounts partition `lineDelta` instead of overlapping it: `dropped` went
	// to the section's Dropped bucket (lowering working capital, leaving equity alone) and
	// `removed` was written off against equity.
	const effects: AdjustmentEffect[] = [
		{
			kind: "line",
			amount: Currency.subtract(Currency.subtract(lineDelta, droppedDelta), removedDelta),
		},
		{ kind: "dropped", amount: Currency.round(droppedDelta) },
		{ kind: "removed", amount: Currency.round(removedDelta) },
	];
	return effects.filter((effect) => effect.amount !== 0);
};

const adjustmentFromView = (view: FinancialPeriodTypes.LeafView): Adjustment | null => {
	const adjusted = view.adjusted;
	if (adjusted === undefined) return null;

	return {
		value: adjusted.value,
		formula: String(adjusted.formula),
		source: adjusted.createdByUserId != null ? "manual" : "auto",
		note: adjusted.note ?? null,
		effects: adjustmentEffects(view.given ?? Currency.ZERO, adjusted.formula),
	};
};

export const layeredLeafFromView = (view: FinancialPeriodTypes.LeafView): LayeredLeaf => ({
	allowed: view.allowed ?? null,
	given: view.given ?? null,
	extracted: view.output ?? null,
	adjustment: adjustmentFromView(view),
});

export const layeredTotalFromView = (view: FinancialPeriodTypes.RollupView): LayeredTotal => ({
	allowed: view.allowed ?? null,
	given: view.given ?? null,
	extracted: view.output ?? null,
});
